import {urls} from '~/common/evite/urls';

const GCS_PATH_IDS = {
  anonymous_image: 'anonymous_image',
  event_video: 'event_video',
  event_custom_logo: 'event_custom_logo',
  video_preview_image: 'preview_image',
  user_profile_photo: 'user_profile_photo',
  event_photo_insert: 'event_photo_insert',
  event_custom_image: 'event_custom_image',
  premium_custom_stamp: 'premium_custom_stamp',
  feed_photos: 'feed_photos',
  host_gallery_photos: 'host_gallery_photos',
};

/*
API by GCS_PATH_ID:
anonymous_image: Anonymous Image. Used to temporarily hold a UYO/DYO image during no-login event create flow, Step 1.
event_custom_logo: Event custom logo image
	event_id: event ID
premium_custom_stamp: Premium Event Custom Stamp Image
	event_id: event ID
event_photo_insert: Event photo-insert image
	event_id: event ID
	image_id: Required. Send an EMPTY STRING for new upload, or a valid id to replace.
event_custom_image: Event Custom (UYO/DYO) image
	event_id: event ID
event_video: Video invitation segment
	video_id: Video invitation ID
	event_context: event context name
	input_name: video input name (must match the name of an input defined by the pipeline referenced by this video template
event_video_with_render: Video invitation segment, with render-video as a post-upload action
	video_id: Video invitation ID
	event_context: event context name
	input_name: video input name (must match the name of an input defined by the pipeline referenced by this video template
feed_photos: Upload a photo related to a post in the feed for one event
	event_id: event ID
	photo_id: PhotoDS ID referenced within the post
user_profile_photo: Upload a user-profile photo
	user_id: user ID
preview_image: Preview image for one video segment
	video_id: Video invitation ID
	event_context: event context name
video_template_asset: Video Template Asset
	video_template_id: video template ID
	event_context: event context name
	input_name: video input name (must match the name of an input defined by the pipeline referenced by this video template
 */

function getImageKeyFromPath(gcsPath) {
  if (gcsPath) {
    // The last part of the path.
    const m = gcsPath.match(/\w+$/);
    return m ? m[0] : null;
  }
  return undefined;
}

/**
 * The first step of the upload process, returns an uploadRequest object.
 * @param gcsPathId
 * @param params obj should match the gcsPathId (see 'API by GPS_PATH_ID')
 * @param body obj containing the image and its mime type
 * @returns {Promise<Error|*>}
 */
const request_access = async (gcsPathId, params, body) => {
  // Path validation
  if (Object.values(GCS_PATH_IDS).includes(gcsPathId)) {
    const url = urls.get('ajax_upload_request', {
      upload_path: gcsPathId,
      ...params, // we add any extra needed param
    });

    // console.log('Upload access request: ', {url, body});
    const response = await evite.fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const {status, statusText} = response;
      return new Error(`ACCESS REQUEST FAILURE: ${status}, ${statusText}`);
    }
    return response.json();
  }
  return new Error('path does not exist');
};

/**
 * Upload image file with uploadRequest object.
 * @param uploadRequest
 * @param file
 * @param onProgress
 * @param customXHR
 * @returns {Promise<*>}
 */
const upload_file_gcs = async (uploadRequest, file, onProgress, customXHR) => {
  const data = new FormData();
  const xhr = customXHR || new XMLHttpRequest();
  const {upload_url, upload_form} = uploadRequest;
  if (!upload_form) {
    return Promise.resolve(false);
  }
  for (const [key, value] of Object.entries(upload_form)) {
    data.append(key, value);
  }
  data.append('file', file, file.name);
  return new Promise((resolve, reject) => {
    xhr.open('POST', upload_url);
    if (onProgress && xhr.upload) {
      try {
        if (evite.detects.isIE || evite.detects.isEdge) {
          xhr.onprogress = onProgress;
        } else {
          xhr.upload.onprogress = onProgress;
        }
      } catch (e) {
        evite.error(e);
      }
    }
    xhr.addEventListener('error', (e) => {
      reject(e);
    });
    xhr.addEventListener('load', (res) => {
      const {
        target: {status},
      } = res;
      if (status === 200 || status === 201 || status === 204) {
        resolve(true);
      } else {
        evite.error('error uploading file, status: ', status);
        reject(new Error(`error uploading file, status ${status}`));
      }
    });
    xhr.send(data);
  });
};

/**
 * Upload an image to GCS.
 * Wraps the request and upload parts of the upload process.
 *
 * @param gcsPathId like 'event_custom_logo', 'premium_custom_stamp', etc.
 * @param params obj should match the gcsPathId (see 'API by GPS_PATH_ID')
 * @param image, must be a Blob
 * @param redirect true for automatic 'finishing' of images
 * @returns {Promise<{gcsPath: *, url: *}>}
 */
async function gcsUploadImage({
  gcsPathId,
  params = {},
  image,
  redirect = true,
  forceImageKey = null,
  fileExtension = null,
}) {
  const body = {
    redirect,
    mimetype: image.type ? image.type : 'image/jpeg',
    file_extension: fileExtension,
  };

  const uploadRequest = await request_access(gcsPathId, params, body);

  if (uploadRequest instanceof Error) {
    evite.error('FAILED TO GET VALID uploadRequest', uploadRequest);
    throw uploadRequest;
  }
  await upload_file_gcs(uploadRequest, image);

  const gcsPath = uploadRequest.gcs_path;
  const imageKey =
    gcsPathId === GCS_PATH_IDS.anonymous_image || gcsPathId === GCS_PATH_IDS.event_photo_insert
      ? getImageKeyFromPath(gcsPath)
      : forceImageKey;

  return {
    url: uploadRequest.access_url,
    gcsPath,
    imageKey,
  };
}

/**
 * Sends the cropped canvas image to be resized and uploaded to gcs in the backend
 * @param gcsPathId like 'event_custom_logo', 'premium_custom_stamp', etc.
 * @param params obj should match the gcsPathId (see 'API by GPS_PATH_ID')
 * @param image the cropped canvas image in base64
 * @param imageType the image mimetype
 * @param width the desired width of the resized image
 * @param height the desired height of the resized image
 * @param redirect true for automatic 'finishing' of images
 * @param forceImageKey the custom image id if from an existing event, otherwise null
 * @returns {Promise<Error|*>}
 */
const resizeImageAndUploadToGcs = async ({
  gcsPathId,
  params = {},
  image,
  imageType,
  width,
  height,
  redirect = true,
  forceImageKey = null,
}) => {
  if (!Object.values(GCS_PATH_IDS).includes(gcsPathId)) {
    return new Error('path does not exist');
  }
  const url = urls.get('ajax_resize_upload_image', {
    upload_path: gcsPathId,
    ...params, // we add any extra needed param
  });

  const dimensions = {width, height};
  // Fill in the form data to be sent in the ajax call
  const formData = new FormData();
  formData.append('image', image);
  formData.append('dimensions', JSON.stringify(dimensions));
  formData.append('redirect', redirect);
  formData.append('mimetype', imageType);
  formData.append('upload_path', gcsPathId);
  formData.append('force_image_key', forceImageKey);
  const response = await evite.fetch(url, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    const {status, statusText} = response;
    return new Error(`RESIZE AND UPLOAD IMAGE FAILURE: ${status}, ${statusText}`);
  }
  return response.json();
};

// we normally don't use it, because we normally send redirect=true on the request_access, and this will call it for us
const upload_gcs_finish = async (gcs_path) => {
  const url = urls.get('ajax_upload_finish') + gcs_path;
  const response = await evite.fetch(url, {
    method: 'POST',
  });
  if (response.ok) {
    return true;
  }
  return new Error('error while calling ajax_upload_finish');
};

export {
  GCS_PATH_IDS,
  getImageKeyFromPath,
  request_access,
  upload_file_gcs,
  upload_gcs_finish,
  gcsUploadImage,
  resizeImageAndUploadToGcs,
};
